import {createContext, use, useSyncExternalStore} from 'react'

/**
 * The held card's height in pixels, for the cells it can open room in. Only the cell a drag
 * starts in can measure the card, so it writes the height here as well as to
 * `--board-held-card-block-size`, and a cell's card window reads it to keep a room drawn.
 */
export type HeldCardSize = {
  get: () => number
  set: (size: number) => void
  subscribe: (listener: () => void) => () => void
}

export function createHeldCardSize(): HeldCardSize {
  let size = 0
  const listeners = new Set<() => void>()

  return {
    get: () => size,
    set: next => {
      if (next === size) return
      size = next
      for (const listener of listeners) listener()
    },
    subscribe: listener => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

export const HeldCardSizeContext = createContext<HeldCardSize | null>(null)

const subscribeToNothing = () => () => {}

/**
 * The held card's height while `isRead`, and `0` otherwise, so a cell with no room open does not
 * re-render when a drag starts.
 */
export function useHeldCardSize(isRead: boolean): number {
  const store = use(HeldCardSizeContext)

  return useSyncExternalStore(
    store?.subscribe ?? subscribeToNothing,
    () => (isRead ? (store?.get() ?? 0) : 0),
    () => 0,
  )
}
